import * as React from "react";
import { inject } from "mobx-react";
import { forceCheck } from "react-lazyload";
import { useLocation } from "react-router-dom";

import { UitkTab, UitkTabs, UitkTabsType } from "uitk-react-tabs";
import { UitkCardAloha, UitkCardAlohaContentSection } from "uitk-react-cards";
import { UitkHeading } from "uitk-react-text";
import { classNames } from "uitk-react-utils";

import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { BlossomComponentsFactory } from "components/flexFramework/domain/BlossomComponentsFactory";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { InsideTabGroupContext } from "components/shared/InsideTabGroupContext/InsideTabGroupContext";
import LinksGridWithDialog from "components/flexComponents/Links/GridView/LinksGridWithDialog";
import {
  PageBackgroundContext,
  BackgroundColorTokens,
} from "src/components/flexComponents/PageLevelProviders/PageBackgroundColor/PageBackgroundContext";
import { TabContents } from "src/components/flexComponents/TabContents/TabContents";
import { FlexComponent } from "src/typings/flexFramework/FlexComponent";
import { Tab, TabGroupProps } from "./typings";

export const TabGroup = inject("analytics")((props: TabGroupProps) => {
  const { flexComponent, context } = props;
  const groups: Tab[] = flexComponent && flexComponent.groups;
  const model = flexComponent && flexComponent.model;

  if (!(groups && groups.length && model)) {
    return null;
  }

  /**
   * inspects provided components and returns a de-duped array containing only TAB type components that have children
   * @param components
   * @returns FlexComponent[]
   */
  const filterTabs = (components: FlexComponent[]): FlexComponent[] => {
    // Tabs cannot have the same name and must contain at least 1 valid module
    const tabNames = new Set();

    return components.filter((component) => {
      // Verify the items in children are tabs, tab name isn't duplicated, and tab has modules
      if (
        !component.model.fmType ||
        (component.model.fmType && component.model.fmType !== "TAB") ||
        !component.name ||
        tabNames.has(component.name) ||
        !component.modules
      ) {
        return false;
      }

      // Verify the number of valid modules, there's no way to know if the render function of a component
      // will return a null value, so the decision is based on other metadata, as well as verifying the module is supported.
      const validModules = BlossomComponentsFactory.get({ flexComponents: component.modules }).filter(
        (module) => module.shouldRender() && module.getReactComponent()
      );

      if (validModules.length < 1) {
        return false;
      }

      tabNames.add(component.name);

      return true;
    });
  };

  const handleTabSelect = (currentSelectedTab: any): void => {
    const selectedTabName = currentSelectedTab as string;
    if (!selectedTabName) {
      return;
    }

    const selectedTab = filteredTabs.find((tab) => tab.name === selectedTabName);
    if (!selectedTab) {
      return;
    }

    const selectedTabIndex = filteredTabs.indexOf(selectedTab);

    const trackingInfo: FlexTrackingInfo = {
      moduleName: flexComponent.id,
      action: Action.CLICK,
      rfrr: `tab.${selectedTabIndex}`,
    };
    sendDelayedTrackEvent(trackingInfo, props.analytics);
    setCurrentTab(selectedTabName);
  };

  const filteredTabs = filterTabs(flexComponent.groups);
  if (!filteredTabs.length) {
    return null;
  }

  const [currentTab, setCurrentTab] = React.useState(filteredTabs[0].name);

  const { hash } = useLocation();

  React.useEffect(() => {
    if (hash.slice(0, 12) === "#section-tab") {
      const anchorId = `tab-${hash.slice(9)}`;
      const tabAnchor = document.getElementById(anchorId)?.children;
      const tabContent = tabAnchor && tabAnchor[0].textContent;

      if (tabAnchor && tabContent) {
        setCurrentTab(tabContent);
        tabAnchor[0].scrollIntoView();
      }
    }
  }, [hash]);

  React.useEffect(() => {
    // Forces the lazy loaded images check to see if they need to be loaded
    // when the current tab changes
    forceCheck();
  }, [currentTab]);

  const keyHelper = new ItemKeyHelper("tab");
  const { title, view, dynamicHeight, boxType } = model;
  const tabGroupClasses = classNames("TabGroup uitk-cell all-cell-1-1 all-x-padding-three all-y-padding-six", {
    affinityTabGroup: view === "affinity",
    dynamicHeightTabGroup: dynamicHeight,
  });

  if (view === "row-trigger" || view === "standard-link") {
    const linksCardView = view === "standard-link" && filteredTabs.length === 1 ? "no-card" : "";

    return (
      <div
        className={`${tabGroupClasses} ${linksCardView}`}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
      >
        <UitkCardAloha>
          <UitkCardAlohaContentSection>
            <div className="uitk-cell">
              <LinksGridWithDialog view={view} groups={filteredTabs} id={flexComponent.id} context={context} />
            </div>
          </UitkCardAlohaContentSection>
        </UitkCardAloha>
      </div>
    );
  }

  const backgroundColor = React.useContext(PageBackgroundContext);
  const withBorder = backgroundColor === BackgroundColorTokens.BACKGROUND_WHITE;
  const uitkCardClasses = classNames({ cardBorder: boxType === "bordered-cards" });

  return (
    <div className={tabGroupClasses} data-fm={flexComponent.fmId} data-fm-title-id={flexComponent.fmTitleId}>
      <UitkCardAloha border={withBorder} className={uitkCardClasses}>
        <UitkCardAlohaContentSection>
          <UitkHeading tag="h2" size={4}>
            {title}
          </UitkHeading>

          <div className="uitk-cell">
            <InsideTabGroupContext.Provider value>
              <UitkTabs selectedTab={currentTab} onTabSelect={handleTabSelect} tabsType={UitkTabsType.NATURAL}>
                {filteredTabs.map((tab, index) => (
                  <UitkTab
                    name={tab.name}
                    key={keyHelper.next()}
                    targetURI="#"
                    tabLinkAttr={{
                      id: `tab-${flexComponent.id}-tab-${index + 1}`,
                      "data-section-id": `#section-${flexComponent.id}-tab-${index + 1}`,
                    }}
                  >
                    <TabContents flexComponent={tab} context={context} />
                  </UitkTab>
                ))}
              </UitkTabs>
            </InsideTabGroupContext.Provider>
          </div>
        </UitkCardAlohaContentSection>
      </UitkCardAloha>
    </div>
  );
});

export default TabGroup;
